'use client';

import { useSupabase } from '@/app/supabase-provider';
import authStyles from './AuthUI.module.css';
import AuthLegalNotice from '@/components/AuthLegalNotice';
import type { EmailSuggestion } from '@/lib/auth/email-validation';
import {
  getEmailBlurSuggestionDecision,
  emailSchema,
  getEmailSubmitDecision,
} from '@/lib/auth/email-validation';
import { useSearchParams, useRouter } from 'next/navigation';
import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { IconAt, IconLock, IconArrowRight, IconX } from '@tabler/icons-react';
import { verifyEmailOtpAction } from '@/app/(website)/signin/actions';
import { resolveSafeNextPath } from '@/lib/auth/safe-next';

interface AuthUIProps {
  bespoke?: boolean;
  connection?: boolean;
  /** Replaces the email step's title and description, e.g. for a product sign-up. */
  heading?: { title: string; description: string };
  subscribeEmail: (email: string) => Promise<boolean>;
  // Add flag to indicate if email was already subscribed
  emailAlreadySubscribed?: boolean;
  /** Brand and agency accounts sign in without a creator role. */
  purpose?: 'creator' | 'business';
  /** Where to go after sign in, instead of the page's `next` parameter. */
  nextPath?: string;
  initialEmail?: string;
  /** Replaces navigation after sign in, e.g. to continue an in-page flow. */
  onAuthenticated?: () => Promise<void>;
  title?: string;
  description?: string;
  sendOtp?: (email: string) => Promise<{ error: { message: string } | null }>;
  verifyOtpAction?: typeof verifyEmailOtpAction;
  /** A secondary route shown under the email step, e.g. the other account type's sign in. */
  footer?: ReactNode;
  /** Use 'signin' where the form cannot create an account. */
  legalNotice?: 'signup' | 'signin';
}

function signInDestination(
  purpose: 'creator' | 'business',
  next: string | null,
  setupRequired: boolean,
) {
  if (setupRequired) {
    // Keep the journey (bio builder, claim, ChatGPT consent) for the retry screen.
    const safe = resolveSafeNextPath(next);
    return safe
      ? `/signin?creatorSetup=1&next=${encodeURIComponent(safe)}`
      : '/signin?creatorSetup=1';
  }
  return resolveSafeNextPath(next) ?? (purpose === 'business' ? '/brands/dashboard' : '/dashboard');
}

function authCopy(connection: boolean, bespoke: boolean) {
  if (connection)
    return {
      title: 'Your email address',
      description:
        'Already use OwlScran? Use the same email. We’ll send a code, no password needed.',
      emailAction: 'Email me a code',
      codeAction: 'Continue to permissions',
    };
  if (bespoke)
    return {
      title: 'Your account email',
      description: 'We’ll email you a sign-in code. No password needed.',
      emailAction: 'Email me a code',
      codeAction: 'Verify code',
    };
  return {
    title: 'Welcome',
    description: 'Enter your email to continue.',
    emailAction: 'Continue',
    codeAction: 'Verify code',
  };
}

export default function AuthUI({
  subscribeEmail,
  emailAlreadySubscribed = false,
  bespoke = false,
  connection = false,
  heading,
  purpose = 'creator',
  nextPath,
  initialEmail,
  onAuthenticated,
  title,
  description,
  sendOtp,
  verifyOtpAction = verifyEmailOtpAction,
  footer,
  legalNotice = 'signup',
}: AuthUIProps) {
  const copy = {
    ...authCopy(connection, bespoke),
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...heading,
  };
  const { supabase } = useSupabase();
  const router = useRouter();

  const searchParams = useSearchParams();
  const providedEmail = searchParams.get('email');

  const [email, setEmail] = useState(initialEmail || providedEmail || '');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const initialEmailSent = useRef(false);
  // Once the code is accepted, a retry only repeats what follows sign in.
  const authenticated = useRef(false);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<EmailSuggestion | null>(null);
  const [acknowledgedSuggestionForEmail, setAcknowledgedSuggestionForEmail] = useState<
    string | null
  >(null);
  // Start in loading state if we have a provided email
  const [authState, setAuthState] = useState<'input' | 'verify' | 'auto-sending'>('input');

  // Initialize refs array
  useEffect(() => {
    otpInputRefs.current = otpInputRefs.current.slice(0, 6);
    while (otpInputRefs.current.length < 6) {
      otpInputRefs.current.push(null);
    }
  }, []);

  // Navigate when Supabase emits SIGNED_IN after OTP verification completes
  useEffect(() => {
    // An in-page flow decides what happens after sign in.
    if (onAuthenticated) return;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        // Trigger a refresh so server components (e.g., Navbar) pick up the new session
        router.refresh();
      }
    });
    return () => subscription.unsubscribe();
  }, [router, supabase.auth, onAuthenticated]);

  const sendEmail = useCallback(
    async (skipSubscription = false, targetEmail = email) => {
      setLoading(true);
      setError(null);

      try {
        // Only subscribe if not already done and not skipped
        if (!skipSubscription && !emailAlreadySubscribed) {
          try {
            await subscribeEmail(targetEmail);
          } catch (subscriptionError) {
            // Log subscription error but don't block the auth flow
            console.warn('Email subscription failed, continuing with auth:', subscriptionError);
          }
        }

        const { error } = sendOtp
          ? await sendOtp(targetEmail)
          : await supabase.auth.signInWithOtp({
              email: targetEmail,
              options: {
                shouldCreateUser: true,
              },
            });

        if (error) {
          setError(error.message);
          setAuthState('input');
        } else {
          setAuthState('verify');
          setOtpError(null);
          authenticated.current = false;
          // Clear any previous OTP input
          setOtp(['', '', '', '', '', '']);
          // Focus the first OTP input field
          setTimeout(() => {
            otpInputRefs.current[0]?.focus();
          }, 100);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setAuthState('input');
      } finally {
        setLoading(false);
      }
    },
    [email, subscribeEmail, supabase.auth, emailAlreadySubscribed, sendOtp],
  );

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numeric input
    const numericValue = value.replace(/[^0-9]/g, '');

    if (numericValue.length > 1) {
      // A complete autofilled/pasted code replaces all digits, whichever box has focus.
      const startIndex = numericValue.length >= 6 ? 0 : index;
      const characters = numericValue.slice(0, 6).split('');
      const newOtp = [...otp];

      for (let i = 0; i < Math.min(characters.length, 6 - startIndex); i++) {
        const char = characters[i];
        if (char !== undefined) {
          newOtp[startIndex + i] = char;
        }
      }

      setOtp(newOtp);

      // Focus on the next available empty input or the last input
      const nextIndex = Math.min(startIndex + characters.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      // Handle single character input
      const newOtp = [...otp];
      newOtp[index] = numericValue;
      setOtp(newOtp);

      // Move focus to the next input if available
      if (numericValue && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // If current input is empty and backspace is pressed, move to previous input
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Debounced OTP verification to prevent race conditions
  const verifyOtp = useCallback(async () => {
    const otpValue = otp.join('');
    if (!authenticated.current && otpValue.length !== 6) {
      setOtpError('Please enter all 6 digits of your verification code');
      return;
    }

    setVerifyLoading(true);
    setOtpError(null);

    try {
      let creatorSetupRequired = false;
      if (!authenticated.current) {
        const result = await verifyOtpAction(email, otpValue, purpose);
        if (!result.ok) {
          setOtpError(result.error);
          // On error, allow another attempt
          setVerifyLoading(false);
          return;
        }
        authenticated.current = true;
        creatorSetupRequired = result.creatorSetupRequired === true;
      }
      if (onAuthenticated) {
        await onAuthenticated();
        setVerifyLoading(false);
      } else if (creatorSetupRequired || nextPath || purpose === 'business') {
        router.replace(
          signInDestination(purpose, nextPath ?? searchParams.get('next'), creatorSetupRequired),
        );
      } else {
        // The server action sets the session cookies. Refresh so the sign-in
        // page sees the authenticated user and redirects to the dashboard.
        router.refresh();
      }
    } catch (err) {
      setOtpError(
        authenticated.current
          ? 'You’re signed in, but we could not open your account. Please try again.'
          : err instanceof Error
            ? err.message
            : 'Verification failed',
      );
      // On exception, allow retry
      setVerifyLoading(false);
    }
  }, [otp, email, router, verifyOtpAction, purpose, onAuthenticated, nextPath, searchParams]);

  // Improved auto-send logic with immediate loading state
  useEffect(() => {
    const parsedProvidedEmail = providedEmail ? emailSchema.safeParse(providedEmail) : null;

    if (!initialEmailSent.current && parsedProvidedEmail?.success && email === providedEmail) {
      initialEmailSent.current = true;
      setAuthState('auto-sending');
      setLoading(true);
      // Skip subscription since it was likely already done on homepage
      setEmail(parsedProvidedEmail.data);
      sendEmail(true, parsedProvidedEmail.data);
    }
  }, [providedEmail, email, sendEmail]);

  // Debounced auto-verification to prevent race conditions
  useEffect(() => {
    if (authState === 'verify' && otp.every((digit) => digit !== '')) {
      const timer = setTimeout(() => {
        verifyOtp();
      }, 300); // Small delay to prevent race conditions

      return () => clearTimeout(timer);
    }
  }, [authState, otp, verifyOtp]);

  const emailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailSuggestion(null);
    setAcknowledgedSuggestionForEmail(null);
    setError(null);
  };

  const emailBlur = () => {
    const decision = getEmailBlurSuggestionDecision(email, acknowledgedSuggestionForEmail);
    if (decision.type === 'none') {
      setEmailSuggestion(null);
      return;
    }
    setEmail(decision.normalizedEmail);
    setEmailSuggestion(decision.suggestion);
  };

  const applySuggestedEmail = () => {
    if (!emailSuggestion) {
      return;
    }

    setEmail(emailSuggestion.full);
    setEmailSuggestion(null);
    setAcknowledgedSuggestionForEmail(null);
    setError(null);
  };

  // A code belongs to the address it was sent to, so changing the address discards it.
  const changeEmail = () => {
    setOtp(['', '', '', '', '', '']);
    setOtpError(null);
    authenticated.current = false;
    setAuthState('input');
  };

  const resendCode = async () => {
    // Skip subscription on resend
    await sendEmail(true);
  };

  // Auto-sending state for better UX
  const autoSendingScreen = (
    <div className="w-full sm:max-w-md mx-auto relative">
      <div className="px-4 sm:px-8 pt-4 sm:pt-8 pb-4 sm:pb-6 relative z-10">
        <div className="flex flex-col items-center">
          <div className="h-16 w-16 rounded-full bg-brand/10 flex items-center justify-center mb-4">
            <svg
              className="animate-spin h-8 w-8 text-brand"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-center text-slate-900 dark:text-slate-100 mb-2">
            Sending verification code
          </h2>
          <p className="text-center text-slate-600 dark:text-slate-400 mb-6">
            We&apos;re sending a code to{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">{email}</span>
          </p>
          <AuthLegalNotice
            variant={legalNotice}
            className="text-xs text-center text-slate-500 dark:text-slate-500 opacity-80"
          />
        </div>
      </div>
    </div>
  );

  const emailEntryForm = (
    <div className="w-full sm:max-w-md mx-auto relative">
      <div className="px-4 sm:px-8 pt-4 sm:pt-8 pb-4 sm:pb-6 relative z-10">
        <h2 className="text-2xl font-bold text-center text-balance text-slate-900 dark:text-slate-100 mb-2">
          {copy.title}
        </h2>
        <p className="text-center text-slate-600 dark:text-slate-400 mb-6">{copy.description}</p>

        <div className="space-y-5">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const submitDecision = getEmailSubmitDecision(email, acknowledgedSuggestionForEmail);

              if (submitDecision.type === 'invalid') {
                const validationResult = emailSchema.safeParse(email);
                setError(
                  validationResult.success
                    ? 'Please enter a valid email address'
                    : (validationResult.error.issues[0]?.message ??
                        'Please enter a valid email address'),
                );
                return;
              }

              if (submitDecision.type === 'show_suggestion') {
                setEmail(submitDecision.normalizedEmail);
                setEmailSuggestion(submitDecision.suggestion);
                setAcknowledgedSuggestionForEmail(submitDecision.normalizedEmail);
                setError(null);
                return;
              }

              setEmail(submitDecision.normalizedEmail);
              setEmailSuggestion(null);
              sendEmail(false, submitDecision.normalizedEmail); // Don't skip subscription for manual sends
            }}
          >
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconAt className="h-5 w-5 text-brand" />
              </div>
              <input
                className="block w-full pl-10 pr-3 py-4 border rounded-2xl transition-all focus:outline-hidden focus:ring-2 text-base bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-brand/50 focus:border-brand"
                aria-label="Email address"
                placeholder="your@email.com"
                value={email}
                onChange={emailChange}
                onBlur={emailBlur}
                type="email"
                autoComplete="email"
                inputMode="email"
                maxLength={254}
                name="email"
                id="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={loading}
                required
              />
            </div>

            {emailSuggestion && (
              <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                <span>Did you mean </span>
                <span className="font-medium">{emailSuggestion.address}@</span>
                <button
                  type="button"
                  onClick={applySuggestedEmail}
                  className="font-medium text-brand hover:text-brand-strong underline bg-transparent appearance-none border-0 p-0"
                >
                  {emailSuggestion.domain}
                </button>
                <span>?</span>
                <button
                  type="button"
                  onClick={() => {
                    setEmailSuggestion(null);
                    setAcknowledgedSuggestionForEmail(email.trim().toLowerCase());
                  }}
                  className="ml-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 bg-transparent appearance-none border-0 p-0"
                >
                  Dismiss
                </button>
              </div>
            )}

            {error && (
              <div role="alert" className="text-sm text-red-500 mt-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-connection-primary={connection}
              className="w-full flex justify-center items-center space-x-2 py-4 px-4 rounded-2xl font-bold mt-5 bg-brand hover:bg-brand-strong text-brand-fg shadow-xl shadow-brand/25 active:scale-[0.98] transform transition-all duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <span>{copy.emailAction}</span>
              )}
              <IconArrowRight className="h-4 w-4" />
            </button>
          </form>

          <AuthLegalNotice
            variant={legalNotice}
            className="text-xs text-center text-slate-500 dark:text-slate-500 opacity-80 mt-4"
          />
          {footer && (
            <div className="border-t border-slate-200 pt-5 text-center text-sm text-slate-600 dark:border-white/10 dark:text-slate-400 [&_a]:font-semibold [&_a]:text-brand [&_a:hover]:text-brand-strong">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const verificationScreen = (
    <div className="w-full sm:max-w-md mx-auto relative">
      <div className="px-4 sm:px-8 pt-4 sm:pt-8 pb-4 sm:pb-6 relative">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {bespoke ? 'Check your email' : 'Verification code'}
          </h2>
          <button
            aria-label="Change email address"
            onClick={changeEmail}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-transparent"
          >
            {bespoke ? (
              <span className="text-sm font-medium">Change email</span>
            ) : (
              <IconX className="h-5 w-5" />
            )}
          </button>
        </div>

        <div className="flex flex-col items-center">
          <div className="h-16 w-16 rounded-full bg-brand/10 flex items-center justify-center mb-4">
            <IconLock className="h-8 w-8 text-brand" />
          </div>

          <p className="text-center text-slate-600 dark:text-slate-400 mb-6">
            We&apos;ve sent a 6-digit code to{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">{email}</span>
          </p>

          <div className={`flex justify-center gap-2 mb-6 ${bespoke ? authStyles.otp : ''}`}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={(el) => {
                  otpInputRefs.current[index] = el;
                  return undefined;
                }}
                aria-label={`Code digit ${index + 1}`}
                disabled={verifyLoading}
                pattern="[0-9]*"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                value={otp[index]}
                onInput={(e) => {
                  // Keep the browser free to insert the entire OTP. Autofill may update
                  // React's value tracker before the event, bypassing synthetic onChange.
                  const data = (e.nativeEvent as InputEvent).data;
                  handleOtpChange(index, data || e.currentTarget.value);
                }}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasteData = e.clipboardData.getData('text');
                  if (/^\d+$/.test(pasteData)) {
                    handleOtpChange(index, pasteData);
                  }
                }}
                className={`w-12 h-14 text-center text-xl font-semibold border ${
                  otpError ? 'border-red-500' : 'border-slate-200 dark:border-white/10'
                } rounded-2xl bg-slate-50 dark:bg-white/5 text-slate-900 dark:text-slate-100 focus:outline-hidden focus:ring-2 focus:ring-brand focus:border-brand`}
              />
            ))}
          </div>

          {otpError && (
            <div role="alert" className="text-sm text-red-500 mb-4 text-center">
              {otpError}
            </div>
          )}

          <button
            data-connection-primary={connection}
            className={`w-full flex justify-center items-center py-4 px-4 rounded-2xl font-bold ${
              verifyLoading
                ? 'bg-brand text-brand-fg cursor-wait opacity-80'
                : 'bg-brand hover:bg-brand-strong text-brand-fg shadow-xl shadow-brand/25 transform hover:scale-[1.02] active:scale-[0.98]'
            } transition-all duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-brand`}
            onClick={verifyOtp}
            disabled={verifyLoading || otp.some((digit) => digit === '')}
          >
            {verifyLoading ? (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              copy.codeAction
            )}
          </button>

          <div className="w-full flex flex-col items-center mt-8">
            <div className="w-full flex items-center mb-4">
              <div className="grow h-px bg-slate-200 dark:bg-slate-700"></div>
              <span className="px-2 text-sm text-slate-400">Didn&apos;t get a code?</span>
              <div className="grow h-px bg-slate-200 dark:bg-slate-700"></div>
            </div>

            <button
              disabled={loading || verifyLoading}
              onClick={resendCode}
              className="text-brand hover:text-brand-strong font-medium text-sm bg-transparent"
            >
              Resend verification code
            </button>
            <AuthLegalNotice
              variant={legalNotice}
              className="mt-4 text-xs text-center text-slate-500 dark:text-slate-500 opacity-80"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const content = (
    <div className="w-full relative bg-transparent">
      <div className="relative w-full max-w-lg mx-auto px-4 pb-8 min-h-full flex items-start justify-center z-10">
        {authState === 'auto-sending'
          ? autoSendingScreen
          : authState === 'input'
            ? emailEntryForm
            : verificationScreen}
      </div>
    </div>
  );
  return bespoke ? (
    <div data-bespoke-auth className={authStyles.bespoke}>
      {content}
    </div>
  ) : (
    content
  );
}
